import { NetworkStatus, gql } from '@apollo/client';
import { deepRemoveUndefinedAndEmpty, flow, pipe, } from '@helo/utils';
import isEqual from 'lodash/isEqual';
import merge from 'lodash/merge';
import { ContentNodeMapper, applyContentMappersAtLocations, applyVariableMappersAtLocations, mapContentNode, } from '../ContentNode/ContentNode-mapper';
import { ContentNodeUtils } from '../ContentNode/ContentNode.utils';
import { DataTypeConfigUtils } from '../DateTypeConfig/DataTypeConfig.utils';
import { DataTypeSelection } from '../DateTypeConfig/DataTypeSelection';
import { processRuntimeConfig, processRuntimeContentConfigNode, } from '../StateOperations/runtimeProcessing';
import { GraphQLUtils } from '../graphql/graphql-utils';
import { changeGqlQueryOperationName } from './QueryNodeUtils.parts';
import { QueryVariablesUtils } from './QueryVariablesUtils';
const setGqlQueryOperationNameToId = () => (queryNode) => {
    const modifiedGraphqlQuery = changeGqlQueryOperationName(queryNode.graphqlQuery, queryNode.id);
    return {
        ...queryNode,
        graphqlQuery: modifiedGraphqlQuery,
    };
};
const setupQuerySelectionDefinition = () => (queryNode) => {
    const querySelectionDefinition = DataTypeSelection.convertGraphQLQueryToSelection(queryNode.graphqlQuery);
    return {
        ...queryNode,
        querySelectionDefinition,
    };
};
const setupSelectedDataTypeConfigs = (baseDataTypeConfigs) => (queryNode) => {
    const selectedQueryDataType = pipe(baseDataTypeConfigs, DataTypeConfigUtils.find([['typeName', 'Query']]), DataTypeSelection.getSelection(queryNode.querySelectionDefinition));
    const queryContentMappers = queryNode.queryDefinition?.contentMappers;
    let mappersToUse;
    if (queryContentMappers && queryContentMappers.length > 0) {
        mappersToUse = queryContentMappers.map(cm => cm.mapper);
    }
    else {
        mappersToUse = queryNode.mapperConfigs;
    }
    const mappedQueryDataType = DataTypeConfigUtils.mapDataTypeConfig(baseDataTypeConfigs, mappersToUse)(selectedQueryDataType);
    return {
        ...queryNode,
        initialQueryDataTypeConfig: selectedQueryDataType,
        queryDataTypeConfig: mappedQueryDataType,
    };
};
const setupArgumentConfigs = (baseDataTypeConfigs) => (queryNode) => {
    const queryVariableConfigs = GraphQLUtils.getQueryDefinitionVariables(queryNode.graphqlQuery, baseDataTypeConfigs, queryNode.queryDataTypeConfig);
    const queryVariableMappers = queryNode.queryDefinition?.variableMappers;
    let mappersToUse;
    if (queryVariableMappers && queryVariableMappers.length > 0) {
        mappersToUse = queryVariableMappers.map(vm => vm.mapper);
    }
    else {
        mappersToUse = queryNode.mapperConfigs || [];
    }
    const mappedQueryVariableConfigs = queryVariableConfigs.map(DataTypeConfigUtils.mapDataTypeConfig(baseDataTypeConfigs, mappersToUse));
    return {
        ...queryNode,
        queryVariableConfigs: mappedQueryVariableConfigs,
        initialQueryVariableConfigs: queryVariableConfigs,
    };
};
const createQueryVariableContentNode = (config, value = config.state?.asValue) => {
    if (config.ofType?.kind === 'INPUT_OBJECT') {
        return ContentNodeUtils.createInputValueContentNodePreservingInputObjectFields(config, value);
    }
    return ContentNodeUtils.createContentNodeWithSelection(config, value);
};
const setupQueryVariableContentNodes = (baseDataTypeConfigs) => (queryNode) => {
    const queryVariableConfigs = queryNode
        .queryVariableConfigs;
    const initialQueryVariableConfigs = queryNode
        .initialQueryVariableConfigs;
    const queryDefinition = queryNode.queryDefinition;
    const initialQueryVariableContentNodes = initialQueryVariableConfigs.map(config => {
        const contentNode = ContentNodeUtils.createContentNodeWithSelection(config, config.state?.initialValue);
        return processRuntimeContentConfigNode({
            contentNode,
        });
    });
    const queryVariableMappers = queryDefinition?.variableMappers;
    let queryVariableContentNodes;
    if (queryVariableMappers && queryVariableMappers.length > 0) {
        const unmappedContentNodes = queryVariableConfigs.map(config => createQueryVariableContentNode(config));
        const mappedVariableContentNodes = applyVariableMappersAtLocations(baseDataTypeConfigs, queryVariableMappers)(unmappedContentNodes);
        queryVariableContentNodes = mappedVariableContentNodes.map(node => processRuntimeContentConfigNode({
            contentNode: node,
        }));
    }
    else {
        queryVariableContentNodes = queryVariableConfigs.map(config => {
            const contentNode = createQueryVariableContentNode(config);
            return processRuntimeContentConfigNode({
                contentNode,
            });
        });
    }
    return {
        ...queryNode,
        queryVariableContentNodes,
        initialQueryVariableContentNodes,
    };
};
const updateQueryInputValue = (inputValueContentNode, baseDataTypeConfigs) => (queryNode) => {
    const oldVariables = getMappedVariablesAsObject()(queryNode);
    const updatedQueryVariableContentNodes = queryNode.queryVariableContentNodes.map(contentNode => {
        if (contentNode.kind === 'INPUT_VALUE' &&
            contentNode.key === inputValueContentNode.key) {
            return inputValueContentNode;
        }
        return contentNode;
    });
    const queryVariableMappers = queryNode.queryDefinition?.variableMappers;
    let mappersToUse;
    if (queryVariableMappers && queryVariableMappers.length > 0) {
        mappersToUse = queryVariableMappers.map(vm => vm.mapper);
    }
    else {
        mappersToUse = queryNode.mapperConfigs;
    }
    const updatedinitialQueryVariableContentNodes = updatedQueryVariableContentNodes.map(contentNode => {
        const reverseMappedNode = ContentNodeMapper.reverseMapContentNode(baseDataTypeConfigs, mappersToUse)(contentNode);
        return processRuntimeContentConfigNode({
            contentNode: reverseMappedNode,
        });
    });
    const updatedQueryNode = {
        ...queryNode,
        queryVariableContentNodes: updatedQueryVariableContentNodes,
        initialQueryVariableContentNodes: updatedinitialQueryVariableContentNodes,
    };
    const newVariables = getMappedVariablesAsObject()(updatedQueryNode);
    const variablesChanged = !isEqual(oldVariables, newVariables);
    return {
        ...updatedQueryNode,
        variablesHaveChanged: queryNode.variablesHaveChanged || variablesChanged,
    };
};
const initializeApolloState = () => (queryNode) => {
    return {
        ...queryNode,
        apolloState: {
            data: null,
            loading: false,
            networkStatus: NetworkStatus.loading,
            partial: false,
        },
    };
};
const processContentNode = (baseDataTypeConfigs) => (queryNode) => {
    const rootDataContent = ContentNodeUtils.createContentNode(queryNode.queryDataTypeConfig, queryNode.apolloState.data);
    const processedDataContent = processRuntimeContentConfigNode({
        contentNode: rootDataContent,
    });
    const queryContentMappers = queryNode.queryDefinition?.contentMappers;
    let mappedDataContent;
    if (queryContentMappers && queryContentMappers.length > 0) {
        mappedDataContent = applyContentMappersAtLocations(baseDataTypeConfigs, queryContentMappers)(processedDataContent);
    }
    else {
        mappedDataContent = mapContentNode(baseDataTypeConfigs, queryNode.mapperConfigs)(processedDataContent);
    }
    const processedMappedDataContent = processRuntimeContentConfigNode({
        contentNode: mappedDataContent,
    });
    return {
        ...queryNode,
        queryContentNode: processedMappedDataContent,
    };
};
const createQueryNode = (queryNodeId, baseDataTypeConfigs, mapperConfigs = []) => (queryDefinition) => {
    return pipe({
        id: queryNodeId,
        queryDefinition,
        graphqlQuery: gql(queryDefinition.rawQuery),
        mapperConfigs: mapperConfigs,
        variablesHaveChanged: false,
        hasRun: false,
    }, setGqlQueryOperationNameToId(), setupQuerySelectionDefinition(), setupSelectedDataTypeConfigs(baseDataTypeConfigs), setupArgumentConfigs(baseDataTypeConfigs), setupQueryVariableContentNodes(baseDataTypeConfigs), initializeApolloState(), processContentNode(baseDataTypeConfigs), (queryNode) => queryNode);
};
const pruneVariables = (variables, { prune = true, pruneTopLevel = true } = {}) => {
    return Object.fromEntries(Object.entries(variables).reduce((acc, [key, value]) => {
        const prunedValue = prune ? deepRemoveUndefinedAndEmpty(value) : value;
        if (pruneTopLevel && prunedValue === undefined) {
            return acc;
        }
        return [...acc, [key, prunedValue]];
    }, []));
};
const getVariablesAsObject = ({ prune = true, pruneTopLevel = true } = {}) => (queryNode) => {
    const variables = QueryVariablesUtils.getVariablesFromContentNodes(queryNode.initialQueryVariableContentNodes);
    return pruneVariables(variables, { prune, pruneTopLevel });
};
const getMappedVariablesAsObject = ({ prune = true, pruneTopLevel = true } = {}) => (queryNode) => {
    const variables = QueryVariablesUtils.getVariablesFromContentNodes(queryNode.queryVariableContentNodes);
    return pruneVariables(variables, { prune, pruneTopLevel });
};
const applyPermanentFilters = (queryNode) => (variables) => {
    const permanentFilters = queryNode.queryDefinition.permanentFilters;
    if (!permanentFilters || permanentFilters.length === 0) {
        return variables;
    }
    const result = { ...variables };
    for (const permanentFilter of permanentFilters) {
        const { inputVariableKey, filter } = permanentFilter;
        const processedFilter = processRuntimeConfig(filter, { currentQueryNode: queryNode });
        const existingValue = result[inputVariableKey];
        if (existingValue !== undefined &&
            existingValue !== null &&
            typeof existingValue === 'object' &&
            !Array.isArray(existingValue)) {
            result[inputVariableKey] = merge({}, processedFilter, existingValue);
        }
        else if (existingValue === undefined || existingValue === null) {
            result[inputVariableKey] = processedFilter;
        }
    }
    return result;
};
const updateVariables = (variables, baseDataTypeConfigs) => (queryNode) => {
    const oldVariables = getMappedVariablesAsObject({ pruneTopLevel: false })(queryNode);
    const inputValueContentNodes = Object.entries(variables).map(([key, value]) => {
        const config = DataTypeConfigUtils.find([['key', key]])(queryNode.queryVariableConfigs);
        if (!config) {
            throw new Error(`Input value config not found for key: ${key}`);
        }
        return ContentNodeUtils.createContentNodeWithSelection(config, value);
    });
    const updatedQueryNode = inputValueContentNodes.reduce((acc, inputValueContentNode) => QueryNodeUtils.updateQueryInputValue(inputValueContentNode, baseDataTypeConfigs)(acc), queryNode);
    const newVariables = getMappedVariablesAsObject({ pruneTopLevel: false })(updatedQueryNode);
    const variablesChanged = !isEqual(oldVariables, newVariables);
    return {
        ...updatedQueryNode,
        variablesHaveChanged: queryNode.variablesHaveChanged || variablesChanged,
    };
};
const setInitialVariables = (variables) => (queryNode) => {
    return {
        ...queryNode,
        queryVariableContentNodes: QueryVariablesUtils.setInitialVariablesOnContentNodes(variables)(queryNode.queryVariableContentNodes),
        initialQueryVariableContentNodes: QueryVariablesUtils.setInitialVariablesOnContentNodes(variables)(queryNode.initialQueryVariableContentNodes),
        queryVariableConfigs: QueryVariablesUtils.setInitialVariables(variables)(queryNode.queryVariableConfigs),
    };
};
const updateVariable = (locator, value) => (queryNode) => {
    const oldVariables = getMappedVariablesAsObject()(queryNode);
    const updatedQueryNode = {
        ...queryNode,
        queryVariableContentNodes: queryNode.queryVariableContentNodes.map(contentNode => QueryVariablesUtils.updateVariableOnContentNode(locator, value)(contentNode)),
        queryVariableConfigs: queryNode.queryVariableConfigs.map(argConfig => QueryVariablesUtils.updateVariable(locator, value)(argConfig)),
    };
    const newVariables = getMappedVariablesAsObject()(updatedQueryNode);
    const variablesChanged = !isEqual(oldVariables, newVariables);
    return {
        ...updatedQueryNode,
        variablesHaveChanged: queryNode.variablesHaveChanged || variablesChanged,
    };
};
const updateHeaders = (headers) => (queryNode) => ({
    ...queryNode,
    additionalHeaders: headers,
});
const setupApolloObservables = (apolloClient) => (queryNode) => {
    const variables = pipe(queryNode, getVariablesAsObject({ prune: true, pruneTopLevel: false }), applyPermanentFilters(queryNode));
    const observableQuery = apolloClient.watchQuery({
        query: queryNode.graphqlQuery,
        variables,
        context: {
            headers: {
                ...queryNode.additionalHeaders,
            },
        },
    });
    return {
        ...queryNode,
        observableQuery,
    };
};
const handleApolloResponse = (queryNode, baseDataTypeConfigs) => (result) => {
    const rootData = result.data;
    const rootDataContent = ContentNodeUtils.createContentNode(queryNode.initialQueryDataTypeConfig, rootData);
    const processedContentNode = processRuntimeContentConfigNode({
        contentNode: rootDataContent,
    });
    const queryContentMappers = queryNode.queryDefinition.contentMappers;
    let mappedDataContent;
    if (queryContentMappers && queryContentMappers.length > 0) {
        mappedDataContent = applyContentMappersAtLocations(baseDataTypeConfigs, queryContentMappers)(processedContentNode);
    }
    else {
        mappedDataContent = mapContentNode(baseDataTypeConfigs, queryNode.mapperConfigs)(processedContentNode);
    }
    const processedMappedDataContent = processRuntimeContentConfigNode({
        contentNode: mappedDataContent,
    });
    return {
        ...queryNode,
        apolloState: result,
        queryContentNode: processedMappedDataContent,
    };
};
const handleApolloError = (queryNode) => (error) => {
    return {
        ...queryNode,
        apolloState: {
            ...queryNode.apolloState,
            error,
            data: undefined,
            loading: false,
            networkStatus: NetworkStatus.error,
            partial: false,
        },
    };
};
const startQueryAndSubscribe = (onResponse, onError, baseDataTypeConfigs) => (queryNode) => {
    const variables = pipe(queryNode, getVariablesAsObject({ prune: true, pruneTopLevel: false }), applyPermanentFilters(queryNode));
    queryNode.observableQuery.setVariables(variables);
    return {
        ...queryNode,
        variablesHaveChanged: false,
        hasRun: true,
        subscription: queryNode.observableQuery.subscribe({
            next: result => {
                setTimeout(() => {
                    pipe(result, handleApolloResponse(queryNode, baseDataTypeConfigs), onResponse);
                }, 0);
            },
            error: errorResult => {
                setTimeout(() => {
                    pipe(errorResult, handleApolloError(queryNode), onError);
                }, 0);
            },
        }),
    };
};
const runQuery = (apolloClient, baseDataTypeConfigs) => (queryNode) => {
    const variables = pipe(queryNode, getVariablesAsObject({ prune: true, pruneTopLevel: false }), applyPermanentFilters(queryNode));
    return apolloClient
        .query({
        query: queryNode.graphqlQuery,
        variables,
        context: {
            headers: {
                ...queryNode.additionalHeaders,
            },
        },
    })
        .then(result => {
        return result;
    })
        .then(handleApolloResponse(queryNode, baseDataTypeConfigs))
        .then(responseNode => ({
        ...responseNode,
        variablesHaveChanged: false,
        hasRun: true,
    }))
        .catch(error => {
        const errorNode = handleApolloError(queryNode)(error);
        return {
            ...errorNode,
            variablesHaveChanged: false,
            hasRun: true,
        };
    });
};
const updateData = (rootData) => (queryNode) => {
    return {
        ...queryNode,
        apolloState: {
            ...queryNode.apolloState,
            data: rootData,
            partial: queryNode.apolloState.partial ?? false,
        },
    };
};
const refetch = () => (queryNode) => {
    const variables = pipe(queryNode, getVariablesAsObject({ prune: true, pruneTopLevel: false }), applyPermanentFilters(queryNode));
    queryNode.observableQuery.refetch({
        ...variables,
    });
    return {
        ...queryNode,
        variablesHaveChanged: false,
        hasRun: true,
    };
};
const waitForResult = (baseDataTypeConfigs) => async (queryNode) => {
    if (!queryNode)
        return queryNode;
    return new Promise((resolve, reject) => {
        let subscription = null;
        let unsubscribeWhenAssigned = false;
        const safelyUnsubscribe = () => {
            if (subscription) {
                subscription.unsubscribe();
                return;
            }
            unsubscribeWhenAssigned = true;
        };
        subscription = queryNode.observableQuery.subscribe({
            next: (result) => {
                if (result.loading) {
                    return;
                }
                flow(handleApolloResponse(queryNode, baseDataTypeConfigs), result => resolve(result))(result);
                safelyUnsubscribe();
            },
            error: flow(handleApolloError(queryNode), reject),
        });
        if (unsubscribeWhenAssigned) {
            subscription.unsubscribe();
        }
    });
};
const getQueryVariableConfigs = () => (queryNode) => {
    return queryNode.queryVariableConfigs;
};
const resetData = (baseDataTypeConfigs) => (queryNode) => {
    const resetApolloState = {
        data: null,
        loading: false,
        networkStatus: NetworkStatus.loading,
        partial: false,
        dataState: 'empty',
    };
    const rootDataContent = ContentNodeUtils.createContentNode(queryNode.queryDataTypeConfig, null);
    const processedDataContent = processRuntimeContentConfigNode({
        contentNode: rootDataContent,
    });
    const queryContentMappers = queryNode.queryDefinition?.contentMappers;
    let mappedDataContent;
    if (queryContentMappers && queryContentMappers.length > 0) {
        mappedDataContent = applyContentMappersAtLocations(baseDataTypeConfigs, queryContentMappers)(processedDataContent);
    }
    else {
        mappedDataContent = mapContentNode(baseDataTypeConfigs, queryNode.mapperConfigs)(processedDataContent);
    }
    const processedMappedDataContent = processRuntimeContentConfigNode({
        contentNode: mappedDataContent,
    });
    return {
        ...queryNode,
        apolloState: resetApolloState,
        queryContentNode: processedMappedDataContent,
        variablesHaveChanged: true,
    };
};
export const QueryNodeUtils = {
    createQueryNode,
    getVariablesAsObject,
    getMappedVariablesAsObject,
    applyPermanentFilters,
    setInitialVariables,
    updateVariables,
    updateVariable,
    updateHeaders,
    setupApolloObservables,
    startQueryAndSubscribe,
    refetch,
    waitForResult,
    runQuery,
    getQueryVariableConfigs,
    updateQueryInputValue,
    resetData,
};
//# sourceMappingURL=QueryNodeUtils.js.map